import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import DataTable from '../../../../components/DataTable/DataTable';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { useHistory } from 'react-router';
import { useGetAllContracts } from '../../hooks/api/contractsApi';
import { serializeObjectToUrl } from '../../../../utils/urlHelper';
import { LinearProgress, withStyles } from '@material-ui/core';
import DataTableBodyCellIcon from '../../../../components/DataTable/DataTableBody/DataTableBodyCell/DataTableBodyCellIcon';
import DataTableBodyCellGraph from '../../../../components/DataTable/DataTableBody/DataTableBodyCell/DataTableBodyCellGraph';
import DataTableRowHoverButton from '../../../../components/DataTable/DataTableBody/DataTableBodyCell/DataTableRowHoverButton';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import { commodities } from '../../../../constants/commodities';

const StyledDataTable = styled.div`
  tr:hover {
    cursor: pointer;
  }
`;

const initialValues = {
  order: 'asc',
  orderBy: 'contractPeriod',
};

const columns = [
  {
    id: 'contractName',
    label: <FormattedMessage id={'app.contracts.contractName'} />,
    width: '12%',
    align: 'left',
  },
  {
    id: 'contractPeriod',
    label: <FormattedMessage id={'app.contracts.contractPeriod'} />,
    width: '12%',
    align: 'right',
    options: {
      sortable: true,
    },
  },
  {
    id: 'customerName',
    label: <FormattedMessage id={'app.organization'} />,
    width: '17%',
    align: 'left',
  },
  {
    id: 'purchasedTotal',
    label: <FormattedMessage id={'app.contracts.purchasedTotal'} />,
    width: '20%',
  },
  {
    id: 'note',
    label: <FormattedMessage id={'app.contracts.note'} />,
  },
  {
    width: '10%',
    id: 'controls',
    label: '',
  },
];

const contractDetail = 'contract';

const ContractsList = ({ customerId }) => {
  const { getAllContracts, data: contracts, loading, error } = useGetAllContracts();

  const [tableData, setTableData] = useState([]);
  const [orderDirection, setOrderDirection] = useState(initialValues.order);
  const [orderByColumn, setOrderByColumn] = useState(initialValues.orderBy);

  useEffect(() => {
    const query = {
      order: orderDirection,
      orderBy: orderByColumn,
    };
    (async () => {
      await getAllContracts(customerId, serializeObjectToUrl(query));
    })();
  }, [getAllContracts, orderDirection, orderByColumn, customerId]);

  useEffect(() => {
    if (contracts) {
      const output = contracts.map((line) => {
        const commodity = commodities?.[line.commodity.id];
        const StyledProgressBar = withStyles({
          colorPrimary: {
            backgroundColor: `#b8b3ad`,
          },
          barColorPrimary: {
            backgroundColor: `#f24f00`,
          },
        })(LinearProgress);

        const totalPercent = line.percentagePurchasedQuantity;
        return {
          id: line.id,
          contractName: <DataTableBodyCellIcon icon={commodity.icon} text={`${line.commodity.name} ${line.contractPeriod}`} />,
          contractPeriod: line.contractPeriod,
          customerName: line.customer.name,
          purchasedTotal: <DataTableBodyCellGraph graph={<StyledProgressBar variant="determinate" value={totalPercent} />} text={`${totalPercent}%`} />,
          note: line.comment,
          controls: (
            <DataTableRowHoverButton>
              <ArrowForwardIosIcon /> <FormattedMessage id={'app.more'} />
            </DataTableRowHoverButton>
          ),
        };
      });

      setTableData(output);
    }
  }, [contracts]);

  const history = useHistory();

  return (
    <StyledDataTable>
      <DataTable
        displayToolbar={false}
        displayPagination={false}
        lineHover
        columns={columns}
        data={tableData || []}
        loading={loading}
        error={error}
        orderDirection={orderDirection}
        setOrderDirection={setOrderDirection}
        orderByColumn={orderByColumn}
        setOrderByColumn={setOrderByColumn}
        onLineClick={(line) => history.push(`/${contractDetail}/${line.id}`)}
      />
    </StyledDataTable>
  );
};

ContractsList.propTypes = {
  customerId: PropTypes.number,
};

export default ContractsList;
